// Fantasy data layer — thin wrappers over the fantasy_* tables/RPCs. Any
// authenticated user can read/write their own rows via the supabase singleton
// (its JWT rides along regardless of which app store is active). When Supabase
// isn't live (MockStore / e2e), an in-memory store stands in so the screens
// render and Playwright can drive them.
import { supabase } from './supabase';
import type { Podium } from '../state/fantasy';

const USE_SUPABASE = import.meta.env.VITE_USE_SUPABASE === 'true';

export interface Stage1Payload {
  ranked?: string[];                    // 1v1: ordered top-8 team ids
  podiums?: Record<string, Podium>;     // 3v3: group → { first, second }
}
export type Stage2Picks = Record<string, string>; // full match id → winner team id

export interface LeaderboardData {
  players: { userId: string; displayName: string }[];
  stage1: { userId: string; event: string; payload: Stage1Payload }[];
  stage2: { userId: string; event: string; matchId: string; pickedWinner: string }[];
}

// ============================== Mock store ==============================
const MOCK_UID = 'mock-you';
const mock = {
  players: new Map<string, string>([
    [MOCK_UID, 'You'],
    ['u-amy', 'Amy Chen'],
    ['u-ben', 'Ben Diaz'],
  ]),
  stage1: new Map<string, Stage1Payload>(), // `${uid}:${event}`
  stage2: new Map<string, Stage2Picks>(),   // `${uid}:${event}`
};

export async function currentUserId(): Promise<string | null> {
  if (!USE_SUPABASE) return MOCK_UID;
  const { data } = await supabase.auth.getSession();
  return data.session?.user.id ?? null;
}

export async function getMyHandle(): Promise<string | null> {
  if (!USE_SUPABASE) return mock.players.get(MOCK_UID) ?? null;
  const uid = await currentUserId();
  if (!uid) return null;
  const { data } = await supabase.from('fantasy_players').select('display_name').eq('user_id', uid).maybeSingle();
  return (data as { display_name?: string } | null)?.display_name ?? null;
}

export async function setMyHandle(name: string): Promise<void> {
  const clean = name.trim().slice(0, 40);
  if (!clean) throw new Error('Name cannot be empty');
  if (!USE_SUPABASE) { mock.players.set(MOCK_UID, clean); return; }
  const { error } = await supabase.rpc('set_my_fantasy_handle', { p_name: clean });
  if (error) throw new Error(error.message);
}

export async function getMyStage1(event: string): Promise<Stage1Payload | null> {
  if (!USE_SUPABASE) return mock.stage1.get(`${MOCK_UID}:${event}`) ?? null;
  const { data } = await supabase.from('fantasy_stage1').select('payload').eq('event', event).maybeSingle();
  return (data as { payload?: Stage1Payload } | null)?.payload ?? null;
}

export async function saveStage1(event: string, payload: Stage1Payload): Promise<void> {
  if (!USE_SUPABASE) { mock.stage1.set(`${MOCK_UID}:${event}`, payload); return; }
  const { error } = await supabase.rpc('save_fantasy_stage1', { p_event: event, p_payload: payload });
  if (error) throw new Error(error.message);
}

export async function getMyStage2(event: string): Promise<Stage2Picks> {
  if (!USE_SUPABASE) return mock.stage2.get(`${MOCK_UID}:${event}`) ?? {};
  const { data } = await supabase.from('fantasy_stage2').select('match_id, picked_winner').eq('event', event);
  const out: Stage2Picks = {};
  for (const r of (data ?? []) as { match_id: string; picked_winner: string }[]) out[r.match_id] = r.picked_winner;
  return out;
}

export async function saveStage2(event: string, picks: Stage2Picks): Promise<void> {
  if (!USE_SUPABASE) { mock.stage2.set(`${MOCK_UID}:${event}`, { ...picks }); return; }
  const { error } = await supabase.rpc('save_fantasy_stage2', { p_event: event, p_picks: picks });
  if (error) throw new Error(error.message);
}

// All players + predictions for the leaderboard, via the get_fantasy_leaderboard
// RPC. The RPC returns an md5(user_id) ALIAS (never the raw auth UUID) and
// re-applies the lock-gating server-side: others' picks appear only once that
// stage is locked, so this stays sparse until locks pass — as intended. The
// userId here is the opaque alias, used only to group picks → player + key rows.
export async function getLeaderboardData(): Promise<LeaderboardData> {
  if (!USE_SUPABASE) {
    return {
      players: [...mock.players].map(([userId, displayName]) => ({ userId, displayName })),
      stage1: [...mock.stage1].map(([k, payload]) => {
        const [userId, event] = k.split(':');
        return { userId, event, payload };
      }),
      stage2: [...mock.stage2].flatMap(([k, picks]) => {
        const [userId, event] = k.split(':');
        return Object.entries(picks).map(([matchId, pickedWinner]) => ({ userId, event, matchId, pickedWinner }));
      }),
    };
  }
  const { data, error } = await supabase.rpc('get_fantasy_leaderboard');
  if (error) throw new Error(error.message);
  const d = (data ?? {}) as Partial<LeaderboardData>;
  return { players: d.players ?? [], stage1: d.stage1 ?? [], stage2: d.stage2 ?? [] };
}
